import type { SearchFeedsResponse, SearchMessageResponse, UnactivatedMember } from "@storyboard/web-client";
import { DEMO_DISPATCHER_NAME, DEMO_DRIVER_NAME } from "@/hooks/useDemoMode";
import { demoLabelling } from "@/store/demoScope";
import { idx, rowsByIndex, store } from "@/store/store";
import type { Account, DirectWsInvitation, WorkspaceMembership } from "@/store/types";

/** The name the web gives Storyboard's own bot account, which keeps its name in demo mode. */
export const BOT_ACCOUNT_NAME = "Storyboard";

/**
 * What a person reads as in demo mode, by their membership's role: a driver (`limitedMember`) is "Driver", an office
 * user (`member` or `admin`) "Dispatcher". A role not yet known reads "Driver"; both labels are non-identifying.
 */
export function demoNameForRole(role: WorkspaceMembership["role"] | null | undefined): string {
  return role === "member" || role === "admin" ? DEMO_DISPATCHER_NAME : DEMO_DRIVER_NAME;
}

/** An account's role in the demo workspace, from the membership the store holds for it; null until one arrives. */
function roleOfAccount(accountId: string, workspaceId: string): WorkspaceMembership["role"] | null {
  return (
    rowsByIndex("workspaceMembership", idx.membershipsByAccount, accountId).find(
      (membership) => membership.workspaceId === workspaceId,
    )?.role ?? null
  );
}

/**
 * An account as the store keeps it: in demo mode a person's name becomes their role's label, with no first or last
 * name left to derive initials from; a bot, and every account outside demo mode, is returned unchanged.
 *
 * `bot` must already be judged on the real name, since the label replaces it.
 */
export function labelAccount(account: Account): Account {
  const labelling = demoLabelling();
  if (!labelling || account.bot) return account;
  return {
    ...account,
    name: demoNameForRole(roleOfAccount(account.id, labelling.workspaceId)),
    firstName: null,
    lastName: null,
  };
}

/** An invitation as the store keeps it: in demo mode, named by its membership's role even when it had no name. */
export function labelInvitation(invitation: DirectWsInvitation): DirectWsInvitation {
  if (!demoLabelling()) return invitation;
  const membership = store.get("workspaceMembership", invitation.workspaceMembershipId);
  return { ...invitation, name: demoNameForRole(membership?.role) };
}

/**
 * A person's name as shown where it comes beside the account it belongs to (a call party, a search result's author):
 * in demo mode, that account's role label, so the bot keeps its name and a person reads by role.
 */
export function labelPersonName<Name extends string | null | undefined>(
  displayName: Name,
  accountId: string | null | undefined,
): Name | string {
  const labelling = demoLabelling();
  if (!labelling || displayName === BOT_ACCOUNT_NAME) return displayName;
  return demoNameForRole(accountId ? roleOfAccount(accountId, labelling.workspaceId) : null);
}

/**
 * Re-labels the accounts and invitations of memberships just written, by their now-known role. Memberships arrive
 * after accounts in a sync pass, so an account labeled before its membership landed is corrected here.
 */
export function relabelMembers(memberships: readonly WorkspaceMembership[]): void {
  if (!demoLabelling()) return;
  const accountIds = memberships.flatMap((membership) => (membership.accountId ? [membership.accountId] : []));
  store.updateMany("account", accountIds, labelAccount);
  const invitationIds = memberships.flatMap((membership) => [...idx.invitationsByMembership.keys(membership.id)]);
  store.updateMany("directWsInvitation", invitationIds, labelInvitation);
}

/**
 * The channel names a driver's alias feed title ends with: the workspace's configured names, and the alias keys
 * themselves for a workspace that configures none. Longest first, so "Night Dispatch" wins over "Dispatch".
 */
function aliasChannelNames(): string[] {
  const labelling = demoLabelling();
  if (!labelling) return [];
  const names = new Set<string>([
    ...Object.values(labelling.aliasChannelNames),
    ...Object.keys(labelling.aliasChannelNames),
  ]);
  for (const alias of store.getAll("workspaceCommandAlias").values()) names.add(alias.alias);
  return [...names].filter(Boolean).sort((a, b) => b.length - a.length);
}

/** The alias channel title for a channel name in demo mode: "Driver" and the channel's name. */
function demoAliasTitle(channelName: string): string {
  return `${DEMO_DRIVER_NAME} ${channelName}`;
}

/**
 * A feed title as the store keeps it: in demo mode, a title ending with an alias channel name reads "Driver" and
 * that name. A hand-made channel that happens to end the same way is labeled too, which is acceptable.
 */
export function labelFeedTitle(title: string | null): string | null {
  const labelling = demoLabelling();
  if (!title || !labelling) return title;
  const matched = aliasChannelNames().find((name) => title === name || title.endsWith(` ${name}`));
  if (!matched) return title;
  // An alias key reads as its configured channel name, the title the alias stamp gives the same feed.
  return demoAliasTitle(labelling.aliasChannelNames[matched] ?? matched);
}

/** An alias feed's title in demo mode, from the alias it carries: its configured channel name, else the alias key. */
export function demoTitleForAlias(alias: string): string | null {
  const labelling = demoLabelling();
  if (!labelling) return null;
  return demoAliasTitle(labelling.aliasChannelNames[alias] ?? alias);
}

/**
 * A never-logged-in member as the roster reads them: in demo mode, the account's and the invitation's names are the
 * role's label, so the roster never falls back to an email address or phone number.
 */
export function labelUnactivatedMember(member: UnactivatedMember): UnactivatedMember {
  if (!demoLabelling()) return member;
  const name = demoNameForRole(member.role);
  return { ...member, account: { ...member.account, name }, invite: { ...member.invite, name } };
}

/** A message search page in demo mode: each result's author by role and its channel's alias title labeled. */
export function labelMessageSearchPage(page: SearchMessageResponse): SearchMessageResponse {
  if (!demoLabelling()) return page;
  return {
    ...page,
    results: page.results.map((result) => ({
      ...result,
      authorName: labelPersonName(result.authorName, result.authorId),
      feedTitle: labelFeedTitle(result.feedTitle) ?? result.feedTitle,
    })),
  };
}

/** A channel search page in demo mode: each driver's alias channel titled by its label. */
export function labelChannelSearchPage(page: SearchFeedsResponse): SearchFeedsResponse {
  if (!demoLabelling()) return page;
  return {
    ...page,
    results: page.results.map((result) => ({ ...result, title: labelFeedTitle(result.title) ?? result.title })),
  };
}
